import { useUserInfo } from '@Hooks/dataFetching/useUserInfo';
import { AppRoutes } from '@Page/Routes';
import { isNumber } from '@Util/numberUtils';
import { getVersion } from '@Util/version';
import { ErrorBoundary, setUser } from '@tlx/sentry';
import React, { ReactNode, useEffect } from 'react';
import './index.css';
import { PilotFeatureContextProvider } from '@Component/PilotFeatureContext/PilotFeatureContext.tsx';
import { SWRConfig } from 'swr';
import { fetcher as defaultFetcher } from '@tlx/astro-shared';
import { getLocale } from '@tlx/intl';
import { useIntlMessages } from '@Locale/locale.ts';
import { IntlProvider } from 'react-intl';

export function App() {
    return (
        <Layout>
            <AppRoutes />
        </Layout>
    );
}

const Layout = ({ children }: { children: ReactNode }) => {
    return (
        <div className="tsk-canvas">
            <React.StrictMode>
                <SWRConfig value={{ fetcher: defaultFetcher }}>
                    <SentryWrapper>
                        <IntlWrapper>
                            <div className="atl-hidden">Version: {getVersion()}</div>
                            <PilotFeatureContextProvider>{children}</PilotFeatureContextProvider>
                        </IntlWrapper>
                    </SentryWrapper>
                </SWRConfig>
            </React.StrictMode>
        </div>
    );
};

type UnexpectedErrorDisplay = {
    error: Error;
};
function UnexpectedErrorDisplay({ error }: UnexpectedErrorDisplay) {
    return (
        <div className="atl-flex atl-flex-col atl-gap-16">
            <div>Det har skjedd en uforutsett feil.</div>
            <div>{error.toString()}</div>
        </div>
    );
}

const SentryWrapper = ({ children }: { children: React.ReactNode }) => {
    const { employeeId } = useUserInfo();
    useEffect(() => {
        if (isNumber(employeeId)) {
            setUser({ id: employeeId?.toString() });
        } else {
            setUser(null);
        }
    }, [employeeId]);
    return (
        <ErrorBoundary fallback={({ error }) => <UnexpectedErrorDisplay error={error as Error} />}>
            {children}
        </ErrorBoundary>
    );
};

const IntlWrapper = ({ children }: { children: ReactNode }) => {
    const locale = getLocale();
    const messages = useIntlMessages(locale);
    return (
        <IntlProvider locale={locale} messages={messages} defaultLocale={'no'}>
            {children}
        </IntlProvider>
    );
};
